import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import Checkbox from '@/components/ui/Checkbox';
import FullscreenError from '@/components/utils/FullscreenError';
import CreateEventModal from '@/components/admin/events/CreateEventModal.tsx';
import EditEventModal from '@/components/admin/events/EditEventModal.tsx';
import CreateEventSeriesModal from '@/components/admin/events/CreateEventSeriesModal.tsx';
import ImportEventsModal from '@/components/admin/events/ImportEventsModal.tsx';
import EventScopeDialog from '@/components/admin/events/EventScopeDialog.tsx';
import EventSeriesTable from '@/components/admin/events/EventSeriesTable.tsx';
import { faCalendarAlt, faSpinner, faArrowLeft, faPlus, faEdit, faTrash, faExternalLinkAlt, faRepeat, faFileCsv, faBell } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { formatEventDateTime, formatReminderOffsetList } from '@/utils/event-helpers';
import { APIRoutes } from '@/services/api.routes';
import type { Event, EventID, EventSeries, EventSeriesScope, FeatureType } from '@/models';
import { ALL_FEATURE_TYPES, FEATURE_TYPE_LABELS } from '@/models';

const pageSize = 50;

type EventsTab = 'events' | 'series';

export default function AdminEventsList() {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateSeriesModalOpen, setIsCreateSeriesModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [deletingEvent, setDeletingEvent] = useState<EventID | null>(null);
  const [scopeDeleteEvent, setScopeDeleteEvent] = useState<Event | null>(null);
  const [featureFilter, setFeatureFilter] = useState<FeatureType | undefined>(undefined);
  const [futureOnlyFilter, setFutureOnlyFilter] = useState<boolean>(true);
  const [seriesRefreshKey, setSeriesRefreshKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<EventID>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const selectedEvents = events.filter(event => selectedIds.has(event.id));
  const allSelected = events.length > 0 && selectedEvents.length === events.length;

  // Every /event-series endpoint requires PermAdmin, so employees never see the tab
  const tab: EventsTab = isAdmin && searchParams.get('tab') === 'series' ? 'series' : 'events';

  const setTab = (next: EventsTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'series') {
      params.set('tab', 'series');
    } else {
      params.delete('tab');
    }
    setSearchParams(params, { replace: true });
  };

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listEvents({
        pageSize: pageSize,
        next: cursorToken,
        feature: featureFilter,
        futureOrToday: futureOnlyFilter ? true : undefined
      });
      setEvents(response.events);
      setTotal(response.total);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Critical error loading events list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Event-Liste');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, featureFilter, futureOnlyFilter]);

  useEffect(() => {
    void fetchEvents();
  }, [fetchEvents]);

  const handleEventCreated = (newEvent: Event) => {
    setEvents(prev => [newEvent, ...prev]);
    setTotal(prev => prev + 1);
    showSuccessToast('Event erfolgreich erstellt');
  };

  const handleSeriesCreated = (series: EventSeries, seriesEvents: Event[]) => {
    showSuccessToast(`Serie „${series.subject}" erstellt — ${seriesEvents.length} Termin(e) angelegt`);
    setSeriesRefreshKey(prev => prev + 1);
    void fetchEvents();
  };

  const handleImported = () => {
    void fetchEvents();
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setIsEditModalOpen(true);
  };

  const handleEventUpdated = (updatedEvent: Event, scope: EventSeriesScope) => {
    // FOLLOWING/ALL touch many rows at once, so a local patch would show stale data
    if (scope !== 'SINGLE') {
      showSuccessToast('Termine der Serie erfolgreich aktualisiert');
      void fetchEvents();
      return;
    }

    setEvents(prevEvents =>
      prevEvents.map(event =>
        event.id === updatedEvent.id ? updatedEvent : event
      )
    );
    showSuccessToast('Event erfolgreich aktualisiert');
  };

  const deleteEvent = async (eventId: EventID, scope?: EventSeriesScope) => {
    try {
      setDeletingEvent(eventId);
      await APIRoutes.deleteEvent(eventId, scope);

      // A series-wide delete removes rows we don't track locally - refetch instead
      if (scope !== undefined && scope !== 'SINGLE') {
        showSuccessToast('Termine der Serie erfolgreich gelöscht');
        await fetchEvents();
      } else {
        setEvents(prev => prev.filter(event => event.id !== eventId));
        setTotal(prev => prev - 1);
        showSuccessToast('Event erfolgreich gelöscht');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      showErrorToast('Fehler beim Löschen des Events');
      handleAPIError(err);
    } finally {
      setDeletingEvent(null);
      setScopeDeleteEvent(null);
    }
  };

  const handleDeleteEvent = (event: Event) => {
    // Occurrences of a series need the scope question, standalone events don't
    if (event.seriesID !== null) {
      setScopeDeleteEvent(event);
      return;
    }

    if (!confirm('Sind Sie sicher, dass Sie dieses Event löschen möchten?')) {
      return;
    }

    void deleteEvent(event.id);
  };

  const toggleSelected = (eventId: EventID, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(eventId);
      } else {
        next.delete(eventId);
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const count = selectedEvents.length;
    const hasSeriesEvents = selectedEvents.some(event => event.seriesID !== null);
    const seriesHint = hasSeriesEvents
      ? '\n\nTermine aus Serien werden nur einzeln gelöscht, die Serie selbst bleibt bestehen.'
      : '';
    if (!confirm(`Sind Sie sicher, dass Sie ${count} Termin(e) löschen möchten?${seriesHint}`)) {
      return;
    }

    setBulkDeleting(true);
    // without a scope the backend deletes only the given occurrence (SINGLE)
    const results = await Promise.allSettled(selectedEvents.map(event => APIRoutes.deleteEvent(event.id)));
    setBulkDeleting(false);

    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failures.length < count) {
      showSuccessToast(`${count - failures.length} Termin(e) erfolgreich gelöscht`);
    }
    if (failures.length > 0) {
      console.error('Error bulk deleting events:', failures.map(f => f.reason));
      showErrorToast(`${failures.length} von ${count} Terminen konnten nicht gelöscht werden`);
      handleAPIError(failures[0].reason);
    }

    // An emptied page would show the empty state without pagination
    if (failures.length === 0 && count === events.length && currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    } else {
      await fetchEvents();
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Event-Liste nicht verfügbar"
        message={error ?? 'Die Event-Liste konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={() => void fetchEvents()}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to="/admin"
            className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="w-8 h-8 text-purple-600 dark:text-purple-400 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Events ({total})
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <button
                onClick={() => setIsImportModalOpen(true)}
                title="Events aus einer CSV importieren"
                className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors flex items-center hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <FontAwesomeIcon icon={faFileCsv} className="w-4 h-4 mr-2" />
                CSV-Import
              </button>
              <button
                onClick={() => setIsCreateSeriesModalOpen(true)}
                title="Neue Event-Serie erstellen"
                className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors flex items-center hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <FontAwesomeIcon icon={faRepeat} className="w-4 h-4 mr-2" />
                Neue Serie
              </button>
            </>
          )}
          <button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!isAdmin}
            title={isAdmin ? 'Neues Event erstellen' : 'Nur für Administratoren'}
            className={`px-4 py-2 bg-purple-600 text-white rounded-lg transition-colors flex items-center ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-700 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
            Neues Event
          </button>
        </div>
      </div>

      {/* Tabs - the series tab is admin-only */}
      {isAdmin && (
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-6">
            <button
              onClick={() => setTab('events')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors cursor-pointer flex items-center ${
                tab === 'events'
                  ? 'border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2" />
              Einzeltermine
            </button>
            <button
              onClick={() => setTab('series')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors cursor-pointer flex items-center ${
                tab === 'series'
                  ? 'border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={faRepeat} className="w-4 h-4 mr-2" />
              Serien
            </button>
          </nav>
        </div>
      )}

      {tab === 'series' ? (
        <EventSeriesTable onEventsChanged={() => void fetchEvents()} refreshKey={seriesRefreshKey} />
      ) : (
        <>
          {/* Filter Section */}
          <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>

              <Checkbox
                checked={futureOnlyFilter}
                onChange={(checked) => {
                  setFutureOnlyFilter(checked);
                  setCurrentPage(1);
                }}
                colorTheme="purple"
                label="Nur zukünftige Events"
              />

              {/*
                NOTE: this filter is currently broken server-side and always returns zero
                rows - db/event.go matches a top-level "features" field, but the real path
                on Event is "featureFilter.features". Fix belongs in the backend
                (EventFilter.FilterQuery), then this works unchanged.
              */}
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700 dark:text-gray-300">Feature:</label>
                <select
                  value={featureFilter ?? ''}
                  onChange={(e) => {
                    setFeatureFilter(e.target.value ? e.target.value as FeatureType : undefined);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-purple-500 focus:border-purple-500 cursor-pointer"
                >
                  <option value="">Alle Features</option>
                  {ALL_FEATURE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {FEATURE_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>

              {isAdmin && selectedEvents.length > 0 && (
                <button
                  onClick={() => void handleBulkDelete()}
                  disabled={bulkDeleting}
                  className="ml-auto px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg transition-colors flex items-center hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  <FontAwesomeIcon icon={bulkDeleting ? faSpinner : faTrash} className={`w-4 h-4 mr-2 ${bulkDeleting ? 'animate-spin' : ''}`} />
                  Ausgewählte löschen ({selectedEvents.length})
                </button>
              )}
            </div>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-12">
              <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-purple-600 dark:text-purple-400 animate-spin" />
            </div>
          )}

          {error !== null && !criticalError && (
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={() => void fetchEvents()}
                className="mt-2 text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-300 cursor-pointer"
              >
                Erneut versuchen
              </button>
            </div>
          )}

          {!loading && error === null && events.length === 0 && (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faCalendarAlt} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Keine Events gefunden
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Erstellen Sie Ihr erstes Event.
              </p>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                disabled={!isAdmin}
                title={isAdmin ? 'Erstes Event erstellen' : 'Nur für Administratoren'}
                className={`px-6 py-3 bg-purple-600 text-white rounded-lg transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-700 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
                Erstes Event erstellen
              </button>
            </div>
          )}

          {!loading && events.length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      {isAdmin && (
                        <th className="pl-6 pr-2 py-3 w-px">
                          <Checkbox
                            checked={allSelected}
                            onChange={(checked) => setSelectedIds(checked ? new Set(events.map(event => event.id)) : new Set())}
                            disabled={bulkDeleting}
                            colorTheme="purple"
                            ariaLabel="Alle auf dieser Seite auswählen"
                          />
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Betreff
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Start
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ende
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Serie
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Features
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Erinnerungen
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Link
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Erstellt
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {events.map((event) => (
                      <tr
                        key={event.id}
                        className={`transition-colors ${selectedIds.has(event.id) ? 'bg-purple-50 dark:bg-purple-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        {isAdmin && (
                          <td className="pl-6 pr-2 py-4 w-px">
                            <Checkbox
                              checked={selectedIds.has(event.id)}
                              onChange={(checked) => toggleSelected(event.id, checked)}
                              disabled={bulkDeleting}
                              colorTheme="purple"
                              ariaLabel={`„${event.subject}“ auswählen`}
                            />
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {event.subject}
                          </div>
                          {event.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs truncate" title={event.description}>
                              {event.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatEventDateTime(event.start)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatEventDateTime(event.end)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {event.seriesID !== null ? (
                            <div className="flex flex-col items-start gap-1">
                              <Link
                                to={`/admin/event-series/${event.seriesID}`}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 hover:bg-purple-200 dark:hover:bg-purple-800"
                                title="Zur Serie"
                              >
                                <FontAwesomeIcon icon={faRepeat} className="w-3 h-3 mr-1" />
                                Serie
                              </Link>
                              {event.seriesOverridden && (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200"
                                  title="Dieser Termin wurde einzeln angepasst und wird von Änderungen an der Serie nicht mehr überschrieben"
                                >
                                  einzeln angepasst
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 dark:text-gray-600">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {event.featureFilter === null ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                Öffentlich
                              </span>
                            ) : (event.featureFilter.features ?? []).length > 0 ? (
                              event.featureFilter.features.map((f) => (
                                <span
                                  key={f}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                >
                                  {FEATURE_TYPE_LABELS[f]}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(event.reminderMinutes ?? []).length > 0 ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                              title={`Erinnerungsmails ${formatReminderOffsetList(event.reminderMinutes ?? [])} vor Beginn`}
                            >
                              <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-1" />
                              {formatReminderOffsetList(event.reminderMinutes ?? [])}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400 dark:text-gray-600">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {event.link && (
                            <a
                              href={event.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 inline-flex items-center"
                              title={event.link}
                            >
                              <FontAwesomeIcon icon={faExternalLinkAlt} className="w-4 h-4" />
                            </a>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(event.created)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEditEvent(event)}
                              disabled={!isAdmin}
                              className={`text-blue-600 dark:text-blue-400 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer'}`}
                              title={isAdmin ? 'Bearbeiten' : 'Nur für Administratoren'}
                            >
                              <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEvent(event)}
                              disabled={!isAdmin || deletingEvent === event.id}
                              className={`text-red-600 dark:text-red-400 disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:text-red-900 dark:hover:text-red-300 cursor-pointer'}`}
                              title={isAdmin ? 'Löschen' : 'Nur für Administratoren'}
                            >
                              {deletingEvent === event.id ? (
                                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                              ) : (
                                <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <PageBasedPagination
                currentPage={currentPage}
                totalCount={total}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                loading={loading}
              />
            </div>
          )}
        </>
      )}

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onEventCreated={handleEventCreated}
      />

      {/* Create Event Series Modal */}
      <CreateEventSeriesModal
        isOpen={isCreateSeriesModalOpen}
        onClose={() => setIsCreateSeriesModalOpen(false)}
        onSeriesCreated={handleSeriesCreated}
      />

      {/* CSV Import Modal */}
      <ImportEventsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImported={handleImported}
      />

      {/* Edit Event Modal */}
      {editingEvent !== null && (
        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingEvent(null);
          }}
          event={editingEvent}
          onEventUpdated={handleEventUpdated}
        />
      )}

      {/* Scope dialog for deleting an occurrence of a series */}
      {scopeDeleteEvent !== null && (
        <EventScopeDialog
          isOpen={true}
          onClose={() => setScopeDeleteEvent(null)}
          title="Termin löschen"
          description={`„${scopeDeleteEvent.subject}" gehört zu einer Serie. Welche Termine sollen gelöscht werden?`}
          confirmLabel="Löschen"
          onConfirm={(scope) => void deleteEvent(scopeDeleteEvent.id, scope)}
          loading={deletingEvent === scopeDeleteEvent.id}
        />
      )}
    </>
  );
}
